import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip, CircularProgress, Alert, IconButton, MenuItem, Divider, InputAdornment, Checkbox, Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  AccountBalanceWallet as WalletIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material';
import { siteAPI, userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const SitesPage = () => {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [openSiteDialog, setOpenSiteDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedSupervisors, setSelectedSupervisors] = useState([]);
  const { isAdmin, isManager } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');

  const [siteForm, setSiteForm] = useState({
    siteName: '',
    location: '',
    clientName: '',
    projectCode: '',
    startDate: new Date().toISOString().split('T')[0],
    status: 'active',  // ⬅️ ADD THIS
    projectValue: 0,
    projectedBudget: 0,
    budgetAlertThreshold: 80,
    notes: '',
    loaDate: '',
    estimatedEndDate: ''
  });

  const [loaFile, setLoaFile] = useState(null);
  const [existingLoaDocument, setExistingLoaDocument] = useState(null);

  // End date change reason prompt
  const [endDateReasonOpen, setEndDateReasonOpen] = useState(false);
  const [endDateChangeReason, setEndDateChangeReason] = useState('');
  const [originalEstimatedEndDate, setOriginalEstimatedEndDate] = useState('');

  // History dialog
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [endDateHistory, setEndDateHistory] = useState([]);

  const [assignForm, setAssignForm] = useState({
    userId: '',
    permissions: 'edit'
  });

  useEffect(() => {
    fetchSites();
    fetchSupervisors();
  }, []);

  const fetchSites = async () => {
    try {
      setLoading(true);
      const response = await siteAPI.getAll();
      const sitesData = response.data.data.sites;

      // Calculate budget details for each site
      const sitesWithBudget = sitesData.map(site => ({
        ...site,
        budgetDetails: calculateBudgetDetails(site)
      }));

      setSites(sitesWithBudget);
    } catch (err) {
      setError('Failed to load sites');
    } finally {
      setLoading(false);
    }
  };

  const calculateBudgetDetails = (site) => {
    const actualSpending = parseFloat(site.actualSpending) || 0;
    const projectedBudget = parseFloat(site.projectedBudget) || 0;

    if (projectedBudget <= 0) {
      if (actualSpending > 0) {
        return { percentageUsed: 0, budgetStatus: 'no_budget', actualSpending, remaining: 0 };
      }
      return null;
    }

    const remaining = projectedBudget - actualSpending;
    const percentageUsed = (actualSpending / projectedBudget) * 100;

    let budgetStatus = 'under_budget';
    if (percentageUsed >= 100) {
      budgetStatus = 'over_budget';
    } else if (percentageUsed >= (site.budgetAlertThreshold || 80)) {
      budgetStatus = 'near_limit';
    }

    return { percentageUsed, budgetStatus, actualSpending, remaining };
  };

  const fetchSupervisors = async () => {
    try {
      const response = await userAPI.getAll({ role: 'supervisor' });
      setSupervisors(response.data.data.users);
    } catch (err) {
      console.error('Failed to fetch supervisors');
    }
  };

  const toggleSupervisorSelection = (supervisorId) => {
    setSelectedSupervisors(prev => {
      if (prev.includes(supervisorId)) {
        return prev.filter(id => id !== supervisorId);
      } else {
        return [...prev, supervisorId];
      }
    });
  };

  const handleAssignMultipleSupervisors = async () => {
    if (selectedSupervisors.length === 0) {
      setError('Please select at least one supervisor');
      return;
    }

    try {
      await siteAPI.assignMultipleSupervisors(selectedSite.id, {
        supervisorIds: selectedSupervisors,
        permissions: 'edit'
      });

      setSuccess(`${selectedSupervisors.length} supervisor(s) assigned successfully!`);
      setSelectedSupervisors([]);
      fetchSites();

      setTimeout(() => {
        setOpenAssignDialog(false);
        setSuccess('');
      }, 1500);

    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign supervisors');
    }
  };

  const handleRemoveSupervisor = async (userId) => {
    if (!window.confirm('Remove this supervisor from the site?')) {
      return;
    }

    try {
      await siteAPI.removeSupervisorFromSite(selectedSite.id, userId);
      setSuccess('Supervisor removed successfully!');
      fetchSites();

      setSelectedSite(prev => ({
        ...prev,
        assignedUsers: prev.assignedUsers.filter(u => u.id !== userId)
      }));

    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove supervisor');
    }
  };

  const handleEditSite = (site) => {
    setIsEditMode(true);
    setSelectedSite(site);
    setSiteForm({
      siteName: site.siteName || '',
      location: site.location || '',
      clientName: site.clientName || '',
      projectCode: site.projectCode || '',
      startDate: site.startDate ? site.startDate.split('T')[0] : new Date().toISOString().split('T')[0],
      status: site.status || 'active',
      projectValue: site.projectValue || 0,
      projectedBudget: site.projectedBudget || 0,
      budgetAlertThreshold: site.budgetAlertThreshold || 80,
      notes: site.notes || '',
      loaDate: site.loaDate ? site.loaDate.split('T')[0] : '',
      estimatedEndDate: site.estimatedEndDate ? site.estimatedEndDate.split('T')[0] : ''
    });
    setExistingLoaDocument(site.loaDocument || null);
    setLoaFile(null);
    setOriginalEstimatedEndDate(site.estimatedEndDate ? site.estimatedEndDate.split('T')[0] : '');
    setEndDateChangeReason('');
    setOpenSiteDialog(true);
  };

  const handleStatusChange = async (siteId, newStatus) => {
    try {
      await siteAPI.updateStatus(siteId, newStatus);
      setSuccess('Site status updated successfully!');
      fetchSites();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleCreateSite = async (e) => {
    e.preventDefault();
    setError('');

    // If editing and the estimated end date actually changed, require a reason first
    if (isEditMode && siteForm.estimatedEndDate !== originalEstimatedEndDate && !endDateChangeReason) {
      setEndDateReasonOpen(true);
      return;
    }

    try {
      const formData = new FormData();
      Object.keys(siteForm).forEach(key => {
        if (siteForm[key] !== undefined && siteForm[key] !== null) {
          formData.append(key, siteForm[key]);
        }
      });
      if (loaFile) formData.append('loaDocument', loaFile);
      if (isEditMode && siteForm.estimatedEndDate !== originalEstimatedEndDate) {
        formData.append('endDateChangeReason', endDateChangeReason);
      }

      if (isEditMode && selectedSite) {
        await siteAPI.update(selectedSite.id, formData);
        setSuccess('Site updated successfully!');
      } else {
        await siteAPI.create(formData);
        setSuccess('Site created successfully with budget tracking!');
      }
      setOpenSiteDialog(false);
      setIsEditMode(false);
      setSelectedSite(null);
      setLoaFile(null);
      setEndDateChangeReason('');
      fetchSites();
      setSiteForm({
        siteName: '',
        location: '',
        clientName: '',
        projectCode: '',
        startDate: new Date().toISOString().split('T')[0],
        status: 'active',
        projectValue: 0,
        projectedBudget: 0,
        budgetAlertThreshold: 80,
        notes: '',
        loaDate: '',
        estimatedEndDate: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save site');
    }
  };

  const handleConfirmEndDateReason = () => {
    if (!endDateChangeReason.trim()) {
      setError('A reason is required to change the Estimated End Date');
      return;
    }
    setEndDateReasonOpen(false);
    // Resubmit now that we have the reason
    handleCreateSite({ preventDefault: () => { } });
  };

  const handleViewHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/sites/${selectedSite.id}/end-date-history`,
        { headers: { Authorization: `Bearer ${token}` } });
      setEndDateHistory(res.data.data.history || []);
    } catch (err) {
      setError('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleAssignSupervisor = async (e) => {
    e.preventDefault();
    setError('');

    // Validate form
    if (!assignForm.userId) {
      setError('Please select a supervisor');
      return;
    }

    if (!selectedSite || !selectedSite.id) {
      setError('No site selected');
      return;
    }

    try {
      console.log('Assigning supervisor:', {
        siteId: selectedSite.id,
        userId: assignForm.userId,
        permissions: assignForm.permissions
      });

      await siteAPI.assignSupervisor(selectedSite.id, assignForm);
      setSuccess('Supervisor assigned successfully!');
      setOpenAssignDialog(false);
      fetchSites();
      setAssignForm({ userId: '', permissions: 'edit' });
      setSelectedSite(null);
    } catch (err) {
      console.error('Assignment error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to assign supervisor');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
      case 'in_progress':
        return 'success';
      case 'completed':
        return 'info';
      case 'on_hold':
      case 'stopped':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getBudgetStatusBadge = (budgetDetails) => {
    if (!budgetDetails) {
      return (
        <Chip
          label="No Budget"
          size="small"
          variant="outlined"
        />
      );
    }

    const { budgetStatus, percentageUsed } = budgetDetails;

    switch (budgetStatus) {
      case 'no_budget':
        return (
          <Chip
            label="No Budget Set"
            size="small"
            variant="outlined"
          />
        );
      case 'over_budget':
        return (
          <Chip
            label={`❌ Over Budget (${isFinite(percentageUsed) ? percentageUsed.toFixed(1) : '∞'}%)`}
            color="error"
            size="small"
          />
        );
      case 'near_limit':
        return (
          <Chip
            label={`⚠️ Near Limit (${percentageUsed.toFixed(1)}%)`}
            color="warning"
            size="small"
          />
        );
      case 'under_budget':
        return (
          <Chip
            label={`✅ Under Budget (${percentageUsed.toFixed(1)}%)`}
            color="success"
            size="small"
          />
        );
      default:
        return (
          <Chip
            label="Unknown"
            size="small"
          />
        );
    }
  };

  const canManageSites = isAdmin || isManager;

  const filteredSites = sites.filter(site => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      site.siteName?.toLowerCase().includes(q) ||
      site.location?.toLowerCase().includes(q) ||
      site.clientName?.toLowerCase().includes(q) ||
      site.projectCode?.toLowerCase().includes(q) ||
      site.status?.toLowerCase().includes(q) ||
      site.assignedUsers?.some(u => u.name?.toLowerCase().includes(q))
    );
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Sites</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search sites..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          {canManageSites && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenSiteDialog(true)}>
              Create Site
            </Button>
          )}
        </Box>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Site Name</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Project Code</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>Estimated End Date</TableCell>
              <TableCell>Completed On</TableCell>
              <TableCell>LOA</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Budget Status</TableCell> {/* NEW COLUMN */}
              <TableCell>Supervisors</TableCell>
              {canManageSites && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredSites.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  No sites found
                </TableCell>
              </TableRow>
            ) : (
              filteredSites.map((site) => (
                <TableRow key={site.id} hover>
                  <TableCell>
                    <Button
                      component={RouterLink}
                      to={`/sites/${site.id}`}
                      variant="text"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {site.siteName}
                    </Button>
                  </TableCell>
                  <TableCell>{site.location}</TableCell>
                  <TableCell>{site.clientName || '-'}</TableCell>
                  <TableCell>{site.projectCode || '-'}</TableCell>
                  <TableCell>{site.startDate ? new Date(site.startDate).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    {site.status !== 'completed' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {site.estimatedEndDate ? new Date(site.estimatedEndDate).toLocaleDateString() : '-'}
                        {site.latestEndDateReason && (
                          <Tooltip title={site.latestEndDateReason}>
                            <InfoOutlinedIcon fontSize="inherit" sx={{ color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        )}
                      </Box>
                    )}
                    {site.status === 'completed' && (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {site.status === 'completed' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          {site.completionDate ? new Date(site.completionDate).toLocaleDateString() : '-'}
                        </Typography>
                        {site.latestEndDateReason && (
                          <Tooltip title={site.latestEndDateReason}>
                            <InfoOutlinedIcon fontSize="inherit" sx={{ color: 'text.secondary', cursor: 'help' }} />
                          </Tooltip>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>                <TableCell>
                    {site.loaDocument ? (
                      <a href={site.loaDocument} target="_blank" rel="noopener noreferrer">View</a>
                    ) : (
                      <Typography variant="caption" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {canManageSites ? (
                      <TextField
                        select
                        size="small"
                        value={site.status}
                        onChange={(e) => handleStatusChange(site.id, e.target.value)}
                        sx={{ minWidth: 140 }}
                        variant="outlined"
                      >
                        <MenuItem value="active">
                          <Chip label="Active" color="success" size="small" />
                        </MenuItem>
                        <MenuItem value="in_progress">
                          <Chip label="In Progress" color="success" size="small" />
                        </MenuItem>
                        <MenuItem value="on_hold">
                          <Chip label="On Hold" color="warning" size="small" />
                        </MenuItem>
                        <MenuItem value="stopped">
                          <Chip label="Stopped" color="warning" size="small" />
                        </MenuItem>
                        <MenuItem value="completed">
                          <Chip label="Completed" color="info" size="small" />
                        </MenuItem>
                      </TextField>
                    ) : (
                      <Chip
                        label={site.status}
                        color={getStatusColor(site.status)}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {getBudgetStatusBadge(site.budgetDetails)}
                  </TableCell>
                  <TableCell>
                    {site.assignedUsers && site.assignedUsers.length > 0 ? (
                      site.assignedUsers.map(user => user.name).join(', ')
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No supervisor
                      </Typography>
                    )}
                  </TableCell>
                  {canManageSites && (
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleEditSite(site)}
                        title="Edit Site"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedSite(site);
                          setOpenAssignDialog(true);
                        }}
                        title="Assign Supervisor"
                      >
                        <PersonAddIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Site Dialog - WITH BUDGET FIELDS */}
      <Dialog open={openSiteDialog} onClose={() => { setOpenSiteDialog(false); setIsEditMode(false); setSelectedSite(null); }} maxWidth="md" fullWidth>
        <form onSubmit={handleCreateSite}>
          <DialogTitle>{isEditMode ? 'Edit Site' : 'Create New Site'}</DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Basic Information Section */}
            <Typography variant="h6" sx={{ mt: 2, mb: 2 }}>
              Basic Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Site Name"
                  value={siteForm.siteName}
                  onChange={(e) => setSiteForm({ ...siteForm, siteName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Location"
                  value={siteForm.location}
                  onChange={(e) => setSiteForm({ ...siteForm, location: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Client Name"
                  value={siteForm.clientName}
                  onChange={(e) => setSiteForm({ ...siteForm, clientName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Project Code"
                  value={siteForm.projectCode}
                  onChange={(e) => setSiteForm({ ...siteForm, projectCode: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={siteForm.startDate}
                  onChange={(e) => setSiteForm({ ...siteForm, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="LOA Date (Optional)"
                  value={siteForm.loaDate}
                  onChange={(e) => setSiteForm({ ...siteForm, loaDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <Button variant="outlined" component="label" fullWidth>
                  {loaFile ? loaFile.name : (existingLoaDocument ? 'Replace LOA Document (PDF)' : 'Upload LOA Document (Optional, PDF)')}
                  <input type="file" hidden accept="application/pdf" onChange={(e) => setLoaFile(e.target.files[0])} />
                </Button>
                {existingLoaDocument && !loaFile && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    <a href={existingLoaDocument} target="_blank" rel="noopener noreferrer">View current LOA document</a>
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={isEditMode ? 8 : 12}>
                <TextField
                  fullWidth
                  type="date"
                  label="Estimated End Date *"
                  value={siteForm.estimatedEndDate}
                  onChange={(e) => setSiteForm({ ...siteForm, estimatedEndDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                  disabled={isEditMode && selectedSite?.status === 'completed'}
                  helperText={isEditMode && selectedSite?.status === 'completed' ? 'Locked — site is marked Completed' : ''}
                />
              </Grid>
              {isEditMode && (
                <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button size="small" onClick={handleViewHistory}>View Change History</Button>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={siteForm.status || 'active'}
                  onChange={(e) => setSiteForm({ ...siteForm, status: e.target.value })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="on_hold">On Hold</MenuItem>
                  <MenuItem value="stopped">Stopped</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </TextField>
              </Grid>

            </Grid>

            {/* Budget Information Section */}
            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <WalletIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                Budget Information
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Track project budget and get alerts when spending approaches limits
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Project Value"
                  value={siteForm.projectValue}
                  onChange={(e) => setSiteForm({ ...siteForm, projectValue: parseFloat(e.target.value) || 0 })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Total contract value from client"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Projected Budget"
                  value={siteForm.projectedBudget}
                  onChange={(e) => setSiteForm({ ...siteForm, projectedBudget: parseFloat(e.target.value) || 0 })}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText="Your internal budget estimate"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Budget Alert Threshold"
                  value={siteForm.budgetAlertThreshold}
                  onChange={(e) => setSiteForm({ ...siteForm, budgetAlertThreshold: parseInt(e.target.value) || 80 })}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  inputProps={{ min: 1, max: 100 }}
                  helperText="Alert when spending reaches this % (default: 80%)"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Budget Notes"
                  value={siteForm.notes}
                  onChange={(e) => setSiteForm({ ...siteForm, notes: e.target.value })}
                  placeholder="Additional notes about budget constraints, project scope, or special considerations..."
                  helperText="Optional notes about the budget"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenSiteDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" startIcon={isEditMode ? <EditIcon /> : <AddIcon />}>
              {isEditMode ? 'Save Changes' : 'Create Site'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Assign Supervisor Dialog */}
      {/* Manage Supervisors Dialog - UPDATED FOR MULTIPLE SELECTION */}
      <Dialog open={openAssignDialog} onClose={() => {
        setOpenAssignDialog(false);
        setSelectedSupervisors([]);
        setSelectedSite(null);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>
          Manage Supervisors for {selectedSite?.siteName}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {/* Currently Assigned Supervisors */}
          {selectedSite?.assignedUsers && selectedSite.assignedUsers.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Currently Assigned ({selectedSite.assignedUsers.length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {selectedSite.assignedUsers.map((user) => (
                  <Box
                    key={user.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'background.paper'
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {user.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveSupervisor(user.id)}
                      title="Remove supervisor"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
              <Divider sx={{ my: 2 }} />
            </Box>
          )}

          {/* Add New Supervisors */}
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Add Supervisors
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {supervisors
              .filter(supervisor => {
                const assignedIds = selectedSite?.assignedUsers?.map(u => u.id) || [];
                // Only show users with role 'supervisor' AND not already assigned
                return supervisor.role === 'supervisor' && !assignedIds.includes(supervisor.id);
              })
              .map((supervisor) => (
                <Box
                  key={supervisor.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: selectedSupervisors.includes(supervisor.id) ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    cursor: 'pointer',
                    bgcolor: selectedSupervisors.includes(supervisor.id) ? 'action.selected' : 'background.paper',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                  onClick={() => toggleSupervisorSelection(supervisor.id)}
                >
                  <Checkbox
                    checked={selectedSupervisors.includes(supervisor.id)}
                    onChange={() => toggleSupervisorSelection(supervisor.id)}
                  />
                  <Box sx={{ ml: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {supervisor.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {supervisor.email}
                    </Typography>
                  </Box>
                </Box>
              ))}

            {supervisors.filter(s => {
              const assignedIds = selectedSite?.assignedUsers?.map(u => u.id) || [];
              return !assignedIds.includes(s.id);
            }).length === 0 && (
                <Alert severity="info">
                  All available supervisors are already assigned to this site.
                </Alert>
              )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenAssignDialog(false);
            setSelectedSupervisors([]);
            setSelectedSite(null);
          }}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignMultipleSupervisors}
            disabled={selectedSupervisors.length === 0}
            startIcon={<PersonAddIcon />}
          >
            Assign ({selectedSupervisors.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* End Date Change Reason Dialog */}
      <Dialog open={endDateReasonOpen} onClose={() => setEndDateReasonOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reason for Changing Estimated End Date</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Changing from <strong>{originalEstimatedEndDate || 'not set'}</strong> to <strong>{siteForm.estimatedEndDate}</strong>. Please provide a reason for this change.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason *"
            value={endDateChangeReason}
            onChange={(e) => setEndDateChangeReason(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndDateReasonOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmEndDateReason}>Confirm & Save</Button>
        </DialogActions>
      </Dialog>

      {/* End Date History Dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Estimated End Date — Change History</DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
          ) : endDateHistory.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No changes recorded yet.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {endDateHistory.map(h => (
                <Paper key={h.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="body2">
                    <strong>{h.oldDate ? new Date(h.oldDate).toLocaleDateString() : 'Not set'}</strong> → <strong>{new Date(h.newDate).toLocaleDateString()}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{h.reason}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {h.changer?.name || 'Unknown'} • {new Date(h.createdAt || h.created_at).toLocaleString()}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SitesPage;